import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  TextField, 
  InputAdornment, 
  Box, 
  Button, 
  Paper, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Select, 
  MenuItem, 
  FormControl, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination, 
  IconButton,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../auth/useAuth';
import { useProject } from '../contexts/ProjectContext';
import AdminLayout from '../components/AdminLayout';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DownloadIcon from '@mui/icons-material/Download';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupIcon from '@mui/icons-material/Group';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router-dom';

const BusList = () => {
  const { api } = useAuth();
  const { activeProject } = useProject();
  const navigate = useNavigate();
  
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [selectedBusImage, setSelectedBusImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [directionFilter, setDirectionFilter] = useState('all');

  // === Validations Modal State ===
  const [validationsModalOpen, setValidationsModalOpen] = useState(false);
  const [validationsLoading, setValidationsLoading] = useState(false);
  const [validations, setValidations] = useState([]);
  const [validationsBus, setValidationsBus] = useState(null);

  const fetchBuses = useCallback(async (search, page, pageSize) => {
    if (!activeProject) {
      console.log('אין פרויקט פעיל');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get('/buses', {
        params: {
          search,
          page: page + 1,
          limit: pageSize,
          showUnassigned: showUnassigned ? 'true' : 'false',
          direction: directionFilter !== 'all' ? directionFilter : undefined,
        },
      });
      
      setBuses(response.data.data || []);
      setTotalRows(response.data.pagination?.totalItems || 0);
    } catch (error) {
      console.error('שגיאה בטעינת האוטובוסים:', error);
      setBuses([]);
    } finally {
      setLoading(false);
    }
  }, [api, activeProject, showUnassigned, directionFilter]);

  const debouncedFetchBuses = useCallback(
    debounce((search, page, pageSize) => fetchBuses(search, page, pageSize), 300),
    [fetchBuses]
  );

  useEffect(() => {
    debouncedFetchBuses(searchTerm, page, rowsPerPage);
  }, [debouncedFetchBuses, searchTerm, page, rowsPerPage]);

  useEffect(() => {
    if (activeProject) {
      setPage(0);
      debouncedFetchBuses(searchTerm, 0, rowsPerPage);
    }
  }, [activeProject, debouncedFetchBuses, searchTerm, rowsPerPage]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleShowUnassignedChange = (event) => {
    setShowUnassigned(event.target.value === 'true');
    setPage(0);
  };

  const handleDirectionFilterChange = (event) => {
    setDirectionFilter(event.target.value);
    setPage(0);
  };

  const handleDeleteBus = async (busId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את האוטובוס?')) {
      try {
        await api.delete(`/buses/${busId}`);
        debouncedFetchBuses(searchTerm, page, rowsPerPage);
      } catch (error) {
        console.error('שגיאה במחיקת האוטובוס:', error);
        alert('שגיאה במחיקת האוטובוס');
      }
    }
  };

  const handleEditBus = (busId) => {
    navigate(`/edit-bus/${busId}`);
  };

  const handleViewImage = (bus) => {
    setSelectedBusImage(bus);
    setImageModalOpen(true);
  };

  const handleDownloadImage = () => {
    if (selectedBusImage?.closureImageUrl) {
      const link = document.createElement('a');
      link.href = selectedBusImage.closureImageUrl;
      link.download = `bus-${selectedBusImage.busNumber}-closure.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // שליחה מחדש של תיקופים
  const handleResendValidations = async (busId) => {
    if (!window.confirm('האם אתה בטוח שברצונך לשלוח מחדש את כל התיקופים של האוטובוס?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.post(`/buses/${busId}/resend-validations`);
      alert(`נשלחו מחדש ${response.data.resentCount} תיקופים מתוך ${response.data.totalPassengers}`);
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('שגיאות בשליחה מחדש:', response.data.errors);
      }
    } catch (error) {
      console.error('שגיאה בשליחה מחדש של התיקופים:', error);
      alert('שגיאה בשליחה מחדש של התיקופים');
    } finally {
      setLoading(false);
    }
  };

  // שליפת תיקופים לאוטובוס
  const fetchBusValidations = async (bus) => {
    try {
      setValidationsLoading(true);
      setValidationsBus(bus);
      // אם הנתיב שלך שונה, עדכן כאן:
      const res = await api.get(`/passengers/bus/${bus.id}`);
      setValidations(res.data?.data || res.data || []);
      setValidationsModalOpen(true);
    } catch (e) {
      console.error('שגיאה בטעינת תיקופים:', e);
      alert('שגיאה בטעינת התיקופים לאוטובוס');
    } finally {
      setValidationsLoading(false);
    }
  };

  const closeValidationsModal = () => {
    setValidationsModalOpen(false);
    setValidations([]);
    setValidationsBus(null);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('he-IL');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString;
  };

  const getDirectionLabel = (direction) => {
    switch(direction) {
      case 'H': return 'הלוך';
      case 'R': return 'חזור';
      default: return direction || '-';
    }
  };

  const getDirectionColor = (direction) => {
    switch(direction) {
      case 'H': return 'primary';
      case 'R': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (isClosed) => {
    return isClosed ? 'error' : 'success';
  };

  // כרטיסית כיוון לתיקוף
  const getPassengerDirectionChip = (dir) => {
    const label = dir === 'H' ? 'הלוך' : dir === 'C' ? 'חזור' : (dir || '-');
    const color = dir === 'H' ? 'primary' : dir === 'C' ? 'secondary' : 'default';
    return { label, color };
  };

  if (!activeProject) {
    return (
      <AdminLayout>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            אנא בחר פרויקט פעיל
          </Typography>
        </Box>
      </AdminLayout>
    );
  }

  // בדיקה האם המסך הוא מובייל
  const isMobile = window.innerWidth <= 768;

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
          <DirectionsBusIcon sx={{ fontSize: { xs: 24, md: 40 }, mr: 1, color: 'primary.main' }} />
          <Typography variant={{ xs: 'h5', md: 'h4' }}>
            רשימת אוטובוסים
          </Typography>
        </Box>
        
        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: { xs: 2, md: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 2 }, 
            alignItems: 'center', 
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%'
          }}>
            <TextField
              placeholder="חיפוש..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: '100%', sm: 300 }
              }}
            />
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              width: { xs: '100%', sm: 'auto' },
              flexDirection: { xs: 'row', sm: 'row' }
            }}>
              <FormControl sx={{ flex: { xs: 1, sm: 'initial' }, minWidth: { xs: 0, sm: 150 } }}>
                <Select
                  value={showUnassigned}
                  onChange={handleShowUnassignedChange}
                  displayEmpty
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuItem value={false}>הכל</MenuItem>
                  <MenuItem value={true}>לא מוקצים</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ flex: { xs: 1, sm: 'initial' }, minWidth: { xs: 0, sm: 120 } }}>
                <Select
                  value={directionFilter}
                  onChange={handleDirectionFilterChange}
                  displayEmpty
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuItem value="all">כל הכיוונים</MenuItem>
                  <MenuItem value="H">הלוך</MenuItem>
                  <MenuItem value="R">חזור</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Button
              variant="contained"
              onClick={() => navigate('/open-bus')}
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                ml: { xs: 0, sm: 'auto' }
              }}
              startIcon={<DirectionsBusIcon />}
              size={isMobile ? "small" : "medium"}
            >
              הוסף אוטובוס
            </Button>
          </Box>
        </Paper>

        {/* מובייל: כרטיסים */}
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {loading ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  טוען אוטובוסים...
                </Typography>
              </Paper>
            ) : buses.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <DirectionsBusIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                <Typography variant="body1" color="textSecondary">
                  לא נמצאו אוטובוסים
                </Typography>
              </Paper>
            ) : (
              buses.map((bus) => (
                <Paper key={bus.id} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {bus.busNumber}
                      </Typography>
                      <Chip 
                        label={getDirectionLabel(bus.direction)}
                        color={getDirectionColor(bus.direction)}
                        size="small"
                      />
                      <Chip
                        label={bus.isClosed ? 'סגור' : 'פתוח'}
                        color={getStatusColor(bus.isClosed)}
                        size="small"
                        variant={bus.isClosed ? 'filled' : 'outlined'}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleResendValidations(bus.id)}
                        color="info"
                        disabled={!bus.validationCount || bus.validationCount === 0}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => fetchBusValidations(bus)}
                        color="default"
                        disabled={!bus.validationCount || bus.validationCount === 0}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditBus(bus.id)}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteBus(bus.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      {bus.closureImageUrl && (
                        <IconButton
                          size="small"
                          onClick={() => handleViewImage(bus)}
                          color="secondary"
                        >
                          <PhotoCameraIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.875rem' }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary">נהג:</Typography>
                      <Typography variant="body2">{bus.driverName || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">זמן:</Typography>
                      <Typography variant="body2">{formatTime(bus.time)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">מסלול:</Typography>
                      <Typography variant="body2">{bus.origin} → {bus.destination}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">נוסעים:</Typography>
                      <Typography variant="body2">{bus.validationCount || 0} / {bus.numSeats || 0}</Typography>
                    </Box>
                  </Box>
                  
                  {bus.transitManager && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="textSecondary">
                        אחראי: {bus.transitManager}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))
            )}
          </Box>
        ) : (
          // דסקטופ: טבלה
          <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    מספר אוטובוס
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    כיוון
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    נהג
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    מסלול
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    זמן יציאה
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    מושבים
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    אומתו
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    אחראי מעבר
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    סטטוס
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    פעולות
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        טוען אוטובוסים...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : buses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                      <DirectionsBusIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" color="textSecondary">
                        לא נמצאו אוטובוסים
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  buses.map((bus) => (
                    <TableRow 
                      key={bus.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f8f8f8' },
                        opacity: bus.isClosed ? 0.7 : 1
                      }}
                    >
                      <TableCell align="center">
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {bus.busNumber}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={getDirectionLabel(bus.direction)}
                          color={getDirectionColor(bus.direction)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {bus.driverName || '-'}
                          </Typography>
                          {bus.driverPhone && (
                            <Typography variant="caption" color="textSecondary">
                              <PhoneIcon sx={{ fontSize: 12, mr: 0.5 }} />
                              {bus.driverPhone}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            <LocationOnIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                            {bus.origin || '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ← {bus.destination || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          <Typography variant="body2">
                            {formatTime(bus.time)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {bus.numSeats || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <GroupIcon sx={{ fontSize: 16, mr: 0.5, color: bus.validationCount > 0 ? 'success.main' : 'grey.400' }} />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: bus.validationCount > 0 ? 'success.main' : 'text.secondary'
                            }}
                          >
                            {bus.validationCount || 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {bus.transitManager || bus.currentResponsible?.name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={bus.isClosed ? 'סגור' : 'פתוח'}
                          color={getStatusColor(bus.isClosed)}
                          size="small"
                          variant={bus.isClosed ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="שלח תיקופים מחדש">
                            <IconButton
                              size="small"
                              onClick={() => handleResendValidations(bus.id)}
                              color="info"
                              disabled={!bus.validationCount || bus.validationCount === 0}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="הצג תיקופים">
                            <IconButton
                              size="small"
                              onClick={() => fetchBusValidations(bus)}
                              color="default"
                              disabled={!bus.validationCount || bus.validationCount === 0}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="ערוך">
                            <IconButton
                              size="small"
                              onClick={() => handleEditBus(bus.id)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="מחק">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteBus(bus.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {bus.closureImageUrl && (
                            <Tooltip title="הצג תמונת סגירה">
                              <IconButton
                                size="small"
                                onClick={() => handleViewImage(bus)}
                                color="secondary"
                              >
                                <PhotoCameraIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage={isMobile ? "" : "שורות בעמוד:"}
          labelDisplayedRows={({ from, to, count }) => 
            isMobile ? `${from}-${to} / ${count}` : `${from}-${to} מתוך ${count !== -1 ? count : `יותר מ ${to}`}`
          }
          sx={{
            '.MuiTablePagination-toolbar': {
              paddingLeft: isMobile ? 0 : 2,
              paddingRight: isMobile ? 0 : 2,
            },
            '.MuiTablePagination-selectLabel': {
              display: isMobile ? 'none' : 'block',
            }
          }}
        />

        {/* Modal להצגת תמונת סגירה */}
        <Dialog
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            תמונת סגירת אוטובוס #{selectedBusImage?.busNumber}
          </DialogTitle>
          <DialogContent>
            {selectedBusImage?.closureImageUrl && (
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={selectedBusImage.closureImageUrl}
                  alt={`תמונת סגירה - אוטובוס ${selectedBusImage.busNumber}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>מספר אוטובוס:</strong> {selectedBusImage.busNumber}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>נהג:</strong> {selectedBusImage.driverName || 'לא צוין'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>נסגר על ידי:</strong> {selectedBusImage.closedBy?.firstName} {selectedBusImage.closedBy?.lastName || 'לא ידוע'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>תאריך סגירה:</strong> {formatDateTime(selectedBusImage.closedAt)}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDownloadImage} startIcon={<DownloadIcon />}>
              הורד תמונה
            </Button>
            <Button onClick={() => setImageModalOpen(false)}>
              סגור
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal להצגת תיקופים */}
        <Dialog
          open={validationsModalOpen}
          onClose={closeValidationsModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            תיקופים לאוטובוס #{validationsBus?.busNumber} ({validations?.length || 0})
          </DialogTitle>

          <DialogContent dividers>
            {validationsLoading ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>טוען תיקופים...</Typography>
              </Box>
            ) : validations?.length === 0 ? (
              <Typography variant="body2" color="textSecondary">אין תיקופים להצגה</Typography>
            ) : (
              isMobile ? (
                // מובייל: כרטיסים
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {validations.map((p, idx) => {
                    const { label, color } = getPassengerDirectionChip(p.direction);
                    return (
                      <Paper key={p.id || idx} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" color="textSecondary">#{idx + 1}</Typography>
                          <Chip label={label} color={color} size="small" />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                          <Box>
                            <Typography variant="caption" color="textSecondary">שם נוסע</Typography>
                            <Typography variant="body2">{p.passengerName || '-'}</Typography>
                          </Box>

                          <Box>
                            <Typography variant="caption" color="textSecondary">חברה</Typography>
                            <Typography variant="body2">{p.company || '-'}</Typography>
                          </Box>

                          <Box>
                            <Typography variant="caption" color="textSecondary">ברקוד / הזמנה</Typography>
                            <Typography variant="body2">{p.barcode || '-'}</Typography>
                          </Box>

                          <Box>
                            <Typography variant="caption" color="textSecondary">זמן תיקוף</Typography>
                            <Typography variant="body2">
                              {p.validatedAt ? formatDateTime(p.validatedAt) : '-'}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              ) : (
                // דסקטופ: טבלה
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">#</TableCell>
                        <TableCell>שם נוסע</TableCell>
                        <TableCell>חברה</TableCell>
                        <TableCell>ברקוד / מס’ הזמנה</TableCell>
                        <TableCell align="center">כיוון</TableCell>
                        <TableCell>שעת תיקוף</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validations.map((p, idx) => {
                        const { label, color } = getPassengerDirectionChip(p.direction);
                        return (
                          <TableRow key={p.id || idx}>
                            <TableCell align="center">{idx + 1}</TableCell>
                            <TableCell>{p.passengerName || '-'}</TableCell>
                            <TableCell>{p.company || '-'}</TableCell>
                            <TableCell>{p.barcode || '-'}</TableCell>
                            <TableCell align="center">
                              <Chip label={label} color={color} size="small" />
                            </TableCell>
                            <TableCell>{p.validatedAt ? formatDateTime(p.validatedAt) : '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeValidationsModal}>סגור</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default BusList;
